import { Images, Loader2, CircleX, Icosahedron } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageItem } from '../../types/misc.ts';

// Animation variants for image/mesh thumbnails
const itemAnimationVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
  },
  exit: {
    opacity: 0,
    scale: 0.8,
  },
};

interface AttachmentPreviewProps {
  disabled: boolean;
  /**
   * Whether to render the drag-to-drop overlay prompts ("Add more images
   * here" / "Drop images and 3D models here"). False for parametric chats,
   * which have no file-dropzone — the uploaded-thumbnail strip still renders.
   */
  showDropMessage: boolean;
  isDragging: boolean;
  isDragHover: boolean;
  images: MessageItem[];
  mesh: MessageItem | null;
  dropMessageTransitionClass: string;
  dropMessageOpacityClass: string;
  onMeshRemoved: () => void;
  onImageRemoved: (image: MessageItem) => void;
}

// Presentational top drop-zone overlay: the drag prompts ("Add more images
// here" / "Drop images and 3D models here") and the uploaded-thumbnail strip
// (mesh tile + image tiles, each with its remove button). Extracted verbatim
// from TextAreaChat's `topDropZoneRef` inner markup — no hooks, so no impact
// on the root's hook order. State + handlers stay owned by the root and flow
// in as props.
export function AttachmentPreview({
  disabled,
  showDropMessage,
  isDragging,
  isDragHover,
  images,
  mesh,
  dropMessageTransitionClass,
  dropMessageOpacityClass,
  onMeshRemoved,
  onImageRemoved,
}: AttachmentPreviewProps) {
  if (disabled) {
    return null;
  }

  return (
    <>
      {/* Case 1: Dragging, and items are ALREADY present -> Show "Add more images" prompt.
          Gated on showDropMessage so parametric chats (no dropzone) never show it. */}
      {showDropMessage && isDragging && (images.length > 0 || mesh !== null) ? (
        <div
          className={cn(
            'flex h-full w-full flex-row items-center justify-center gap-2', // Ensure it fills parent
            // Opacity is handled by the parent's transition when it appears/disappears due to isDragging
          )}
        >
          <Images
            className="h-5 w-5"
            style={{
              color: isDragHover ? '#FFBA28' : 'rgba(255, 186, 40, 0.85)',
            }}
          />
          <p
            className="text-sm font-normal"
            style={{
              color: isDragHover ? '#FFBA28' : 'rgba(255, 186, 40, 0.85)',
            }}
          >
            Add more images here
          </p>
        </div>
      ) : /* Case 2: No items (images/mesh are zero) -> Show original "Drop images and 3D models here" logic.
          Gated on showDropMessage so parametric chats render nothing here. */
      showDropMessage && images.length === 0 && mesh === null ? (
        <div
          className={cn(
            'flex h-full w-full flex-row items-center justify-center gap-2', // Ensure it fills parent
            dropMessageTransitionClass,
            dropMessageOpacityClass,
          )}
        >
          <Images
            className="h-5 w-5"
            style={{
              color: isDragHover ? '#FFBA28' : 'rgba(255, 186, 40, 0.85)',
            }}
          />
          <p
            className="text-sm font-normal"
            style={{
              color: isDragHover ? '#FFBA28' : 'rgba(255, 186, 40, 0.85)',
            }}
          >
            Drop images and 3D models here
          </p>
        </div>
      ) : (
        /* Case 3: Items are present, and NOT dragging -> Show thumbnails */
        (images.length > 0 || mesh !== null) && (
          <div
            className={cn(
              'flex w-full items-center gap-4 overflow-x-auto overflow-y-hidden p-4',
              // Opacity dimming logic can remain if desired, or be simplified
              isDragging && (images.length > 0 || mesh !== null)
                ? 'opacity-60'
                : 'opacity-100',
              'transition-opacity duration-150',
            )}
          >
            <AnimatePresence>
              {' '}
              {/* Ensure no initial={false} here */}
              {mesh && (
                <motion.div
                  key={`mesh-${mesh.id}`}
                  className="relative h-12 w-12 flex-shrink-0"
                  variants={itemAnimationVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  layout
                >
                  {mesh.url && (
                    <img
                      src={mesh.url}
                      alt="Mesh"
                      className="h-12 w-12 rounded-md object-cover"
                    />
                  )}
                  {mesh.isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50">
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    </div>
                  )}
                  {!mesh.isUploading && (
                    <div className="absolute bottom-[-0.50rem] right-[-0.50rem] rounded-full border border-telekinesis-neutral-500 bg-telekinesis-neutral-500 text-white transition-colors duration-200 hover:border-telekinesis-neutral-700 hover:bg-telekinesis-neutral-700">
                      <Icosahedron className="h-4 w-4 text-white" />
                    </div>
                  )}
                  <button
                    onClick={onMeshRemoved}
                    disabled={mesh.isUploading}
                    className={cn(
                      'absolute right-[-0.50rem] top-[-0.50rem] rounded-full border border-telekinesis-neutral-500 bg-telekinesis-neutral-500 text-white transition-colors duration-200 hover:border-telekinesis-neutral-700 hover:bg-telekinesis-neutral-700',
                      mesh.isUploading && 'opacity-50',
                    )}
                  >
                    <CircleX className="h-4 w-4 stroke-[1.5]" />
                  </button>
                </motion.div>
              )}
              {images.map((image) => (
                <motion.div
                  key={`image-${image.id}`}
                  className="relative h-12 w-12 flex-shrink-0"
                  variants={itemAnimationVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  layout
                >
                  <img
                    src={image.url}
                    alt="Image"
                    className="h-12 w-12 rounded-md object-cover"
                  />
                  {image.isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50">
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    </div>
                  )}
                  <button
                    onClick={() => onImageRemoved(image)}
                    disabled={image.isUploading}
                    className={cn(
                      'absolute right-[-0.50rem] top-[-0.50rem] rounded-full border border-telekinesis-neutral-500 bg-telekinesis-neutral-500 text-white transition-colors duration-200 hover:border-telekinesis-neutral-700 hover:bg-telekinesis-neutral-700',
                      image.isUploading && 'opacity-50',
                    )}
                  >
                    <CircleX className="h-4 w-4 stroke-[1.5]" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )
      )}
    </>
  );
}
